import React from 'react';
import {
  Box, Typography, Stack, Chip, Paper,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { Referral, ReferralStatus } from '~/types/transition';
import SourceBadge from '../common/SourceBadge';

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

const OutreachStatusChip: React.FC<{ referral: Referral }> = ({ referral }) => {
  if (!referral.autoDeclined) {
    return <Chip label="Manual" size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />;
  }
  if (referral.outreachStatus === 'SENT') {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: '0.75rem !important' }} />}
        label="Outreach Sent"
        size="small"
        color="error"
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
    );
  }
  return (
    <Chip
      icon={<ScheduleIcon sx={{ fontSize: '0.75rem !important' }} />}
      label="Outreach Pending"
      size="small"
      color="warning"
      variant="outlined"
      sx={{ fontSize: '0.7rem', height: 20 }}
    />
  );
};

interface GroupProps {
  title: string;
  subtitle: string;
  referrals: Referral[];
  borderColor: string;
}

const DeclinedGroup: React.FC<GroupProps> = ({ title, subtitle, referrals, borderColor }) => {
  const navigate = useNavigate();
  if (referrals.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
        <Chip label={referrals.length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
      </Stack>
      <Paper variant="outlined" sx={{ borderLeft: `3px solid ${borderColor}` }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '16%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '27%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '14%' }} />
          </colgroup>
          <TableHead>
            <TableRow sx={{ '& .MuiTableCell-root': { py: 1, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' } }}>
              <TableCell>Patient</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Outreach</TableCell>
              <TableCell>Received</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {referrals.map((r) => (
              <TableRow
                key={r.id}
                hover
                onClick={() => navigate(`/transition/referral/${r.id}`)}
                sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.patient.name}</Typography>
                  <Typography variant="caption" color="text.disabled">{r.patient.dob}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.patient.city}, {r.patient.state}</Typography>
                </TableCell>
                <TableCell>
                  <SourceBadge source={r.source} />
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                    {r.autoDeclineReason ?? r.overrideReason ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <OutreachStatusChip referral={r} />
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                    {r.patient.referringFacility}
                  </Typography>
                  {r.outreachSentAt && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                      {formatDate(r.outreachSentAt)}
                    </Typography>
                  )}
                  {!r.outreachSentAt && r.outreachScheduledAt && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                      Scheduled {formatDate(r.outreachScheduledAt)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    {formatDate(r.receivedAt)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const DeclinedReferralsTable: React.FC = () => {
  const { referrals } = useTransitionContext();

  const autoDeclinedPending = referrals.filter(
    (r) => r.autoDeclined && r.outreachStatus === 'PENDING'
  );
  const autoDeclinedSent = referrals.filter(
    (r) => r.autoDeclined && r.outreachStatus === 'SENT'
  );
  const manuallyDeclined = referrals.filter(
    (r) => !r.autoDeclined && r.status === ReferralStatus.DECLINED
  );

  const total = autoDeclinedPending.length + autoDeclinedSent.length + manuallyDeclined.length;

  if (total === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <BlockIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No declined referrals.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <DeclinedGroup
        title="Auto-Declined — Outreach Pending"
        subtitle="Override window is open — no notice sent yet"
        referrals={autoDeclinedPending}
        borderColor="#EF6C00"
      />
      <DeclinedGroup
        title="Auto-Declined — Outreach Sent"
        subtitle="Decline notice delivered to referring provider"
        referrals={autoDeclinedSent}
        borderColor="#C62828"
      />
      <DeclinedGroup
        title="Manually Declined"
        subtitle="Reviewed and declined by a coordinator"
        referrals={manuallyDeclined}
        borderColor="#757575"
      />
    </Box>
  );
};

export default DeclinedReferralsTable;
